import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PreviewHeader from "./preview-header";
import "../css/preview-local.css";
import PreviewContent from "./preview-content";
import ApiGet from "../../../../api-controller-mod/api-serv/get-api/getApi";
import ApiPost from "../../../../api-controller-mod/api-serv/post-api/postApi";
import { ApiResponse, collectStatusUpdatePaths, ContentVersionBase, getContentVersionIndexApi, getSectionByUUID, updateContentVersionApi, WorkflowSubmitPayload } from "../ts/workflow.type";
import {
  normalizerMap,
  compareChangedSectionsGeneric,
  getPageKeyFromVersion,
  NormalizedAnyPage,
} from "../ts/normalise/preview-normaliser.type";
import { API_ENDPOINTS } from "../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider";
import { Button, Confirmation } from "@core/uilibrary";
import Cookies from "js-cookie";
import { useGlobalPopupProvider } from "../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider";
import { useToast } from "../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider";
import { ArrowDownIcon, SuccessDocConfirmationIcon } from "../../../../asset-controller-mod/asset-serv/icons/iconExporter";
import { useEditMode } from "../../../../context-controller-mod/context-serv/global-provider/dirty-provider/dirtyModeProvider";
import { groupPermissionAccessWorkflow } from "../../../../context-controller-mod/context-serv/permission-provider/permissionExporter";

type LocationState = {
  allVersions?: ContentVersionBase[];
};

const isPublishedStatus = (value?: string) => {
  const status = String(value ?? "").toLowerCase();
  return status === "published";
};

const isCompareWithPublishedStatus = (value?: string) => {
  const status = String(value ?? "").toLowerCase();

  return (
    status === "draft" ||
    status === "reviewed" ||
    status === "scheduled" ||
    status === "approved" ||
    status === "rejected" ||
    status === "unpublished" ||
    status === "unpublish"
  );
};

const isUnpublishedStatus = (value?: string) => {
  const status = String(value ?? "").toLowerCase();
  return (
    status === "unpublished" ||
    status === "unpublish" ||
    status === "rejected"
  );
};

const isDraftLikeStatus = (value?: string) => {
  const status = String(value ?? "").toLowerCase();
  return (
    status === "draft" ||
    status === "reviewed" ||
    status === "scheduled" ||
    status === "approved"
  );
};

const buildContentStatusUpdatePayload = (
  page: ContentVersionBase,
  nextStatus: string
) => {
  const pageKey = getPageKeyFromVersion(page);
  const pageData = page?.data ?? {};
  const pageNode = pageData?.[pageKey];

  const statusPaths = collectStatusUpdatePaths(pageData);

  return {
    type: String((pageNode as Record<string, any>)?.type ?? pageKey ?? ""),
    title: String((pageNode as Record<string, any>)?.title ?? ""),
    version: Number(page.version ?? 0),
    updates: statusPaths.map((path) => ({
      path,
      value: nextStatus,
    })),
  };
};

const getSectionIdFromPage = (page: ContentVersionBase | null): string => {
  if (!page?.data) return "";

  const pageKey = getPageKeyFromVersion(page);
  const pageNode = page.data?.[pageKey];

  if (!pageNode) return "";

  // Special case for navigation pages
  if (pageKey === "header") return "header";
  if (pageKey === "footer") return "footer";

  // Keep current logic for all other pages
  if (!Array.isArray(pageNode.cta)) return "";

  for (const item of pageNode.cta) {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const keys = Object.keys(item);
      if (keys.length > 0) {
        return keys[0];
      }
    }
  }

  return "";
};

const PreviewLocal: FC = () => {
  const { currentId, versionId, mode } = useParams<{
    currentId: string;
    versionId: string;
    mode: string;
  }>();

  const [allVersionsState, setAllVersionsState] = useState<ContentVersionBase[]>([]);
  const [initialStatus, setInitialStatus] = useState("");

  const location = useLocation();
  const state = location.state as LocationState | null;
  const allVersions = allVersionsState;
  const navigate = useNavigate();

  const [pageKey, setPageKey] = useState("");
  const [draftData, setDraftData] = useState<NormalizedAnyPage | null>(null);
  const [publishedData, setPublishedData] = useState<NormalizedAnyPage | null>(null);
  const [diffData, setDiffData] = useState<Record<string, { published: unknown; draft: unknown }>>({});
  const [dataPage, setDataPage] = useState<ContentVersionBase | null>(null);
  const [isReady, setIsReady] = useState(false);
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { showToast } = useToast();
  const { setEditing } = useEditMode();

  const [statusValue, setStatusValue] = useState("");
  const [publishDate, setPublishDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const [canScrollDown, setCanScrollDown] = useState(false);
  const pageRef = useRef<HTMLElement | null>(null);

  const canUpdate = groupPermissionAccessWorkflow.update();

  useEffect(() => {
    if (!initialStatus) return;

    if (statusValue !== initialStatus) {
      setEditing(true);
    }
  }, [statusValue, initialStatus]);

  useEffect(() => {
    if (state?.allVersions) {
      setAllVersionsState(state.allVersions);
      return;
    }

    const fetchVersions = async () => {
      try {
        const res = await getContentVersionIndexApi();
        const list = res?.data?.data ?? [];
        setAllVersionsState(list as ContentVersionBase[]);
      } catch (e) {
        console.error("Failed to load versions", e);
      }
    };

    fetchVersions();
  }, [state]);

  useEffect(() => {
    if (!versionId || !mode) return;
    if (!allVersions.length) return;
    const run = async () => { //NOSONAR
      try {
        setIsReady(false);
        setPageKey("");
        setDraftData(null);
        setPublishedData(null);
        setDiffData({});
        setDataPage(null);

        if (!versionId || !mode) {
          console.warn("Missing versionId or mode");
          return;
        }

        const versionRes =
          mode === "published"
            ? await ApiGet<ApiResponse<ContentVersionBase>>(
              `${API_ENDPOINTS.FIND_CONTENT_BY_VERSION}?version=${versionId}`
            )
            : await getSectionByUUID(versionId);

        const selectedVersion = versionRes.data?.data;

        if (!selectedVersion) {
          console.warn("Selected version not found");
          return;
        }

        const normalizedStatus = String(selectedVersion.status ?? "").toLowerCase();

        setStatusValue(normalizedStatus);
        setInitialStatus(normalizedStatus);

        const rawPublishAt = selectedVersion.publishAt;
        const parsedPublishDate = rawPublishAt ? new Date(rawPublishAt) : null;

        setPublishDate(
          parsedPublishDate && !Number.isNaN(parsedPublishDate.getTime())
            ? parsedPublishDate
            : null
        );

        const detectedPageKey = getPageKeyFromVersion(selectedVersion);
        const normalize = normalizerMap[detectedPageKey];

        if (!normalize) {
          console.warn(`No normalizer found for page: ${detectedPageKey}`);
          return;
        }

        if (mode === "published") {
          const isPublishedLike =
            selectedVersion.isPublished === true ||
            isPublishedStatus(selectedVersion.status);

          if (!isPublishedLike) {
            console.warn(
              `URL mode is "published" but fetched version status="${selectedVersion.status}" isPublished=${selectedVersion.isPublished}`
            );
            return;
          }

          const normalizedPublished = normalize(selectedVersion);

          if (!normalizedPublished) {
            console.warn("Published normalization failed");
            return;
          }

          setPageKey(detectedPageKey);
          setPublishedData(normalizedPublished);
          setDraftData(null);
          setDiffData({});
          setDataPage(selectedVersion);
          setIsReady(true);
          return;
        }

        const shouldCompareWithPublished =
          selectedVersion.isPublished === false &&
          isCompareWithPublishedStatus(selectedVersion.status);

        if (!shouldCompareWithPublished) {
          console.warn(
            `URL mode is "${mode}" but fetched version status="${selectedVersion.status}" isPublished=${selectedVersion.isPublished}`
          );
          return;
        }

        const normalizedCurrent = normalize(selectedVersion);


        if (!normalizedCurrent) {
          console.warn("Selected version normalization failed");
          return;
        }

        const publishedVersion = allVersions.find((item) => {
          const sameContent = item.contentUuid === selectedVersion.contentUuid;
          const publishedLike =
            item.isPublished === true || isPublishedStatus(item.status);
          const samePageKey = getPageKeyFromVersion(item) === detectedPageKey;

          return sameContent && publishedLike && samePageKey;
        });

        const normalizedPublished = publishedVersion
          ? normalize(publishedVersion)
          : null;

        const compareResult =
          normalizedPublished && normalizedCurrent
            ? compareChangedSectionsGeneric(normalizedPublished, normalizedCurrent)
            : { diff: {} };

        setPageKey(detectedPageKey);
        setDraftData(normalizedCurrent);
        setPublishedData(normalizedPublished);
        setDiffData(compareResult.diff ?? {});
        setDataPage(selectedVersion);
        setIsReady(true);
      } catch (error) {
        console.error("Failed to load preview", error);
      }
    };

    run();
  }, [currentId, versionId, mode, allVersions]);

  const formatPublishAt = (date: Date | null): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getSubmitPublishAt = (): string | null => {
    const normalized = String(statusValue ?? "").toLowerCase();

    if (normalized === "scheduled") {
      return formatPublishAt(publishDate);
    }

    return formatPublishAt(new Date());
  };

  const mapSubmitStatus = (value: string): string => {
    const normalized = String(value).toLowerCase();

    if (normalized === "scheduled") return "SCHEDULED";
    if (normalized === "published") return "PUBLISHED";
    if (normalized === "approved") return "APPROVED";
    if (normalized === "rejected") return "REJECTED";
    if (normalized === "unpublished" || normalized === "unpublish") return "UNPUBLISHED";
    if (normalized === "reviewed") return "REVIEWED";
    if (normalized === "draft") return "DRAFT";

    return normalized.toUpperCase();
  };

  const handleSave = async () => {
    try {
      if (!dataPage?.contentVersionUuid || !dataPage?.contentUuid) {
        console.warn("Missing content version info");
        return;
      }

      const normalizedStatus = String(statusValue ?? "").toLowerCase();

      if (normalizedStatus === "scheduled" && !publishDate) {
        console.warn("Schedule date is required when status is scheduled");
        return;
      }

      const userUuid = Cookies.get("userUuid");

      if (!userUuid) {
        console.warn("Missing userUuid in cookie");
        return;
      }

      openConfirm(
        <Confirmation
          visible
          title="Save status?"
          description="Are you sure you want to save this action? This action will effect the whole systems."
          status="info"
          onClose={closeConfirm}
          icon={<SuccessDocConfirmationIcon/>}
          buttons={
            <>
              <Button
                variant="ghost"
                size="md"
                value="Cancel"
                type="button"
                disabled={saving}
                onClick={closeConfirm}
              />

              <Button
                variant="primary"
                size="md"
                value={saving ? "Saving..." : "Confirm"}
                type="button"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);

                    const submitStatus = mapSubmitStatus(statusValue);

                    const workflowPayload: WorkflowSubmitPayload = {
                      contentVersionUuid: dataPage.contentVersionUuid,
                      approverUuid: userUuid,
                      requesterUuid: userUuid,
                      contentUuid: dataPage.contentUuid,
                      notifyUuid: userUuid,
                      userUuid,
                      status: submitStatus,
                      publishAt: getSubmitPublishAt(),
                    };

                    const contentUpdatePayload = buildContentStatusUpdatePayload(
                      dataPage,
                      submitStatus
                    );

                    if (contentUpdatePayload.updates.length > 0) {
                      await updateContentVersionApi(
                        dataPage.contentUuid,
                        dataPage.contentVersionUuid,
                        userUuid,
                        contentUpdatePayload
                      );
                    }

                    await ApiPost(API_ENDPOINTS.API_WORKFLOW_SUBMIT, workflowPayload);

                    closeConfirm();

                    showToast({
                      status: "success",
                      title: "Saved",
                      description: "Workflow status has been updated successfully.",
                    });
                    setEditing(false);
                    navigate("/workflow/manage");
                  } catch (error) {
                    console.error("Submit failed", error);

                    showToast({
                      status: "error",
                      title: "Save failed",
                      description: "Unable to save workflow status.",
                    });
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            </>
          }
        />
      );
    } catch (error) {
      console.error("Open confirm failed", error);
    }
  };

  useEffect(() => {
    const checkScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      const hasMoreToScroll = scrollTop + windowHeight < fullHeight - 10;
      setCanScrollDown(hasMoreToScroll);
    };

    checkScroll();

    window.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      window.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [isReady, draftData, publishedData, diffData]);

  const handleScrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  const handleCancel = () => {
    navigate("/workflow/manage")
  }

  return (
    <main className="scl--prev-work-loc-main-sty" ref={pageRef}>
      <PreviewHeader
        pageName={pageKey}
        contentType={pageKey}
        createAt={dataPage?.createdAt}
        author={dataPage?.createdBy}
        pageStatus={dataPage?.status}
        selectedStatus={statusValue}
        selectedDate={publishDate}
        onStatusChange={setStatusValue}
        onDateChange={setPublishDate}
        sectionId={getSectionIdFromPage(dataPage)}
      />

      <PreviewContent
        pageKey={pageKey}
        diffData={diffData}
        draftData={draftData}
        publishedData={publishedData}
        pageStatus={dataPage?.status}
        isReady={isReady}
        mode={mode}
      />

      <div className="button-content-controller">
        <Button
          variant="ghost"
          value="Back"
          onClick={handleCancel}
        />
        {canUpdate &&
          <Button
            variant="primary"
            value={saving ? "Saving..." : "Save"}
            onClick={handleSave}
          />
        }
      </div>

      <div className={`arrow-down ${canScrollDown ? "active" : "disabled"}`}>
        <div className="center">
          <button
            type="button"
            onClick={handleScrollToBottom}
            disabled={!canScrollDown}
          >
            <ArrowDownIcon />
          </button>
        </div>
      </div>
    </main>
  );
};

export default PreviewLocal;